import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  PropertyDto,
  PropertyId,
  PropertyMemberDto,
  PropertyMemberRole,
  PropertyMemberStatus,
  UserDto,
} from '@workspace/data-types';
import { UserStore } from '@workspace/ng-data-access';
import { take, tap } from 'rxjs';
import { PropertyInvitationDetailsDialogComponent } from '../property-invitation-details-dialog/property-invitation-details-dialog.component';

@Component({
  selector: 'etxe-property-pending-invitations-banner',
  standalone: true,
  imports: [CommonModule, DecimalPipe, MatButtonModule, MatIconModule, MatDialogModule],
  templateUrl: './property-pending-invitations-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyPendingInvitationsBannerComponent {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);

  readonly PropertyMemberRole = PropertyMemberRole;

  readonly invitations = input.required<PropertyDto[]>();

  readonly accepted = output<PropertyId>();
  readonly declined = output<PropertyId>();

  getMemberForCurrentUser(inv: PropertyDto): PropertyMemberDto | undefined {
    const user = this.userStore.current();
    const currentUserId = user?._id?.toString();
    const currentUserEmail = user?.contactInfos?.email?.toLowerCase();

    const pending = (inv.members || []).find((m) => {
      if (m.status !== PropertyMemberStatus.PENDING) return false;
      const mUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const mEmail = m.email?.toLowerCase();
      return (
        (currentUserId && mUserId === currentUserId) || (currentUserEmail && mEmail === currentUserEmail)
      );
    });

    if (pending) return pending;

    return (inv.members || []).find((m) => {
      const mUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const mEmail = m.email?.toLowerCase();
      return (
        (currentUserId && mUserId === currentUserId) || (currentUserEmail && mEmail === currentUserEmail)
      );
    });
  }

  getInvitationRole(inv: PropertyDto): PropertyMemberRole {
    return this.getMemberForCurrentUser(inv)?.role || PropertyMemberRole.CO_OWNER;
  }

  getInvitationRoleLabel(inv: PropertyDto): string {
    const role = this.getInvitationRole(inv);
    return role === PropertyMemberRole.VIEWER ? 'Spectateur' : 'Co-vendeur';
  }

  getCoverPhotoUrl(inv: PropertyDto): string | null {
    const cp = inv.media?.coverPhoto;
    if (cp && typeof cp === 'object' && 'url' in cp && typeof cp.url === 'string') {
      return cp.url;
    }
    return null;
  }

  getPropertyTypeLabel(type: string | undefined): string {
    const labels: Record<string, string> = {
      apartment: 'Appartement',
      house: 'Maison',
      land: 'Terrain',
      parking_box: 'Parking / Box',
      business: 'Fonds de commerce',
      professional: 'Immobilier pro',
    };
    return (type && labels[type]) || 'Bien immobilier';
  }

  getDpeColor(energyClass: string | undefined): string {
    const colors: Record<string, string> = {
      A: '#00803C',
      B: '#54B345',
      C: '#CED932',
      D: '#FFF200',
      E: '#FCBA08',
      F: '#EB8235',
      G: '#D71D20',
    };
    return (energyClass && colors[energyClass.toUpperCase()]) || '#9ca3af';
  }

  openDetails(inv: PropertyDto): void {
    const dialogRef = this.dialog.open(PropertyInvitationDetailsDialogComponent, {
      data: inv,
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        tap((action: 'accept' | 'decline' | undefined) => {
          if (action === 'accept') {
            this.accepted.emit(inv._id);
          } else if (action === 'decline') {
            this.declined.emit(inv._id);
          }
        }),
      )
      .subscribe();
  }
}
